import { useCallback, useMemo, useState } from 'react';
import { useRerollCharacterItemAffixMutation, useUpgradeCharacterItemMutation } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useMutationError } from './use-mutation-error';

/** Why an upgrade or reroll failed, and on which item: shown only there (#330). */
export interface ItemCraftingError {
    readonly itemId: string;
    readonly action: 'upgrade' | 'reroll';
    readonly message: string;
}

export interface ItemCraftingState {
    /** Busy with any upgrade or reroll, through a whole run of upgrade levels. */
    isWorking: boolean;
    /** Why the last upgrade or reroll failed, or null. */
    error: ItemCraftingError | null;
    /** Raises an item one upgrade level. Resolves to whether it succeeded. */
    upgrade: (itemId: string) => Promise<boolean>;
    /**
     * Raises an item `levels` upgrade levels, one request a level (the server
     * upgrades one at a time), stopping at the first that fails. Resolves to
     * how many landed.
     */
    upgradeBy: (itemId: string, levels: number) => Promise<number>;
    /** Rolls one of an item's affixes again. Resolves to whether it succeeded. */
    reroll: (itemId: string, affixIndex: number) => Promise<boolean>;
}

/** Upgrading a character's items and rerolling their affixes, paid from banked essence. */
export function useItemCrafting(characterId: string | null): ItemCraftingState {
    const accountId = getStoredCanonicalAccountId();
    const [upgradeItem, { isLoading: isUpgrading }] = useUpgradeCharacterItemMutation();
    const [rerollAffix, { isLoading: isRerolling }] = useRerollCharacterItemAffixMutation();
    const { error: message, refuse, run: runRequest } = useMutationError();
    // Which item and action the message is about, set as each request starts.
    const [failedOn, setFailedOn] = useState<Omit<ItemCraftingError, 'message'> | null>(null);
    // Busy across a run of upgrades, not only during each request.
    const [isUpgradingLevels, setIsUpgradingLevels] = useState(false);

    const run = useCallback((itemId: string, action: ItemCraftingError['action'], request: (accountId: string, characterId: string) => Promise<unknown>, fallback: string): Promise<boolean> => {
        setFailedOn({ itemId, action });
        return accountId === null || characterId === null
            ? refuse('You need to be signed in to work on items.')
            : runRequest(() => request(accountId, characterId), fallback);
    }, [accountId, characterId, refuse, runRequest]);

    const upgrade = useCallback((itemId: string): Promise<boolean> => run(
        itemId,
        'upgrade',
        (account, character) => upgradeItem({ accountId: account, characterId: character, itemId }).unwrap(),
        'The upgrade failed. Try again.',
    ), [run, upgradeItem]);

    const upgradeBy = useCallback(async (itemId: string, levels: number): Promise<number> => {
        setIsUpgradingLevels(true);
        try {
            let landed = 0;
            while (landed < levels && await upgrade(itemId)) {
                landed += 1;
            }
            return landed;
        } finally {
            setIsUpgradingLevels(false);
        }
    }, [upgrade]);

    const reroll = useCallback((itemId: string, affixIndex: number): Promise<boolean> => run(
        itemId,
        'reroll',
        (account, character) => rerollAffix({ accountId: account, characterId: character, itemId, affixIndex }).unwrap(),
        'The reroll failed. Try again.',
    ), [rerollAffix, run]);

    const error = useMemo(() => (message === null || failedOn === null ? null : { ...failedOn, message }), [failedOn, message]);
    return { isWorking: isUpgrading || isRerolling || isUpgradingLevels, error, upgrade, upgradeBy, reroll };
}
